/* global __BUILD_TAG__ */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, NavLink, Link, useNavigate, useLocation, matchPath } from 'react-router-dom';
import {
  OFERTA_PATH,
  OPERATOR_EMAIL,
  OPERATOR_INN,
  OPERATOR_NAME,
  OPERATOR_OGRNIP,
} from '../legal/operator';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { WorkChromeProvider, useWorkChrome } from '../context/WorkChromeContext';
import { TabIdentityProvider } from '../context/TabIdentityContext';
import WorkToolbar from './WorkToolbar';
import UserAccountMenu from './UserAccountMenu';
import CookieNotice, { openCookieSettings } from './CookieNotice';
import SiteNoticesBanner from './SiteNoticesBanner';
import { fetchActiveLessonSession } from '../api/activeLesson';
import { getStudentNavItems } from '../studentHub';
import { getTeacherNavItems } from '../teacherHub';
import useVisitHeartbeat from '../hooks/useVisitHeartbeat';

const BUILD_TAG = typeof __BUILD_TAG__ !== 'undefined' ? __BUILD_TAG__ : 'режим разработки (без сборки)';

function isCollabChromePath(pathname) {
  return Boolean(
    matchPath({ path: '/collaborative', end: true }, pathname) ||
      matchPath({ path: '/collaborative/:sessionId', end: true }, pathname),
  );
}

function NavIcon({ icon }) {
  return <i className={`fa-solid ${icon} nav-link-icon`} aria-hidden />;
}

function HubNavLink({ item }) {
  const location = useLocation();
  if (item.external) {
    return (
      <a
        className="nav-link"
        href={item.external}
        target="_blank"
        rel="noopener noreferrer"
        title={item.title || item.label}
      >
        <NavIcon icon={item.icon} />
        {item.label}
      </a>
    );
  }

  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) => {
        const active = item.match ? item.match(location.pathname) : isActive;
        return [
          'nav-link',
          item.live ? 'nav-link--live' : '',
          active ? 'active' : '',
        ]
          .filter(Boolean)
          .join(' ');
      }}
      title={item.title || item.label}
    >
      <NavIcon icon={item.icon} />
      {item.label}
    </NavLink>
  );
}

function LayoutInner() {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { toolbarProps } = useWorkChrome();
  const isDark = theme === 'dark';
  const collabChrome = isCollabChromePath(location.pathname);
  const [activeLesson, setActiveLesson] = useState(null);
  const [navOpen, setNavOpen] = useState(false);
  const navRef = useRef(null);
  const menuBtnRef = useRef(null);

  const isTeacherCabinet = Boolean(user?.is_teacher || user?.is_staff || user?.is_admin);
  const isStudent = Boolean(user?.is_student);
  const isParentOnly = Boolean(user?.is_parent && !isStudent && !isTeacherCabinet);

  const studentNavItems = useMemo(
    () => (isStudent ? getStudentNavItems({ user, activeLesson }) : []),
    [isStudent, user, activeLesson],
  );
  const teacherNavItems = useMemo(
    () => (!isStudent && isTeacherCabinet ? getTeacherNavItems() : []),
    [isStudent, isTeacherCabinet],
  );

  useEffect(() => {
    if (!isStudent || collabChrome) {
      setActiveLesson(null);
      return undefined;
    }
    let cancelled = false;
    const load = () => {
      fetchActiveLessonSession()
        .then((active) => {
          if (!cancelled) setActiveLesson(active);
        })
        .catch(() => {
          if (!cancelled) setActiveLesson(null);
        });
    };
    load();
    // Пока ученик в кабинете — чтобы пункт «Урок» появился без смены страницы.
    const id = setInterval(load, 10000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [isStudent, collabChrome, location.pathname]);

  async function handleLogout() {
    try {
      await logout();
      navigate('/login');
    } catch {
      navigate('/login');
    }
  }

  useVisitHeartbeat(user);

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!navOpen) return undefined;
    function onPointerDown(event) {
      const target = event.target;
      if (navRef.current?.contains(target) || menuBtnRef.current?.contains(target)) return;
      setNavOpen(false);
    }
    function onKeyDown(event) {
      if (event.key === 'Escape') setNavOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [navOpen]);

  return (
    <div className={`layout${collabChrome ? ' layout--collab-chrome' : ''}`}>
      <header className="header">
        <div className="header-inner">
          <NavLink to="/" className="logo" title="Дистанционное обучение">
            <span className="logo-icon">📘</span>
            <span className="logo-text">
              <span className="logo-en">
                diobuch{collabChrome ? null : <span className="logo-version"> v2.0</span>}
              </span>
              <span className="logo-ru">Диобуч</span>
            </span>
          </NavLink>

          {collabChrome ? (
            <nav className="nav nav--collab-chrome" aria-label="Быстрая навигация">
              {user?.is_student && (
                <HubNavLink
                  item={{
                    id: 'konspekty',
                    label: 'Конспекты',
                    icon: 'fa-book',
                    to: '/konspekty',
                  }}
                />
              )}
              {isTeacherCabinet && (
                <HubNavLink
                  item={{
                    id: 'students',
                    label: 'Ученики',
                    icon: 'fa-user-group',
                    to: '/teacher',
                  }}
                />
              )}
            </nav>
          ) : (
            <nav
              id="site-nav"
              ref={navRef}
              className={navOpen ? 'nav nav-open' : 'nav'}
              aria-label="Основная навигация"
            >
              {isStudent &&
                studentNavItems.map((item) => (
                  <HubNavLink key={item.id} item={item} />
                ))}
              {!isStudent &&
                teacherNavItems.map((item) => (
                  <HubNavLink key={item.id} item={item} />
                ))}
              {isParentOnly && (
                <>
                  <HubNavLink
                    item={{
                      id: 'parent-schedule',
                      label: 'Расписание',
                      icon: 'fa-calendar-days',
                      to: '/my-schedule',
                    }}
                  />
                  <HubNavLink
                    item={{
                      id: 'billing',
                      label: 'Баланс',
                      icon: 'fa-wallet',
                      to: '/billing',
                    }}
                  />
                  <HubNavLink
                    item={{
                      id: 'parent-messages',
                      label: 'Сообщения',
                      icon: 'fa-envelope',
                      to: '/my-messages',
                    }}
                  />
                  <HubNavLink
                    item={{
                      id: 'parent',
                      label: 'Дети',
                      icon: 'fa-children',
                      to: '/parent',
                    }}
                  />
                </>
              )}
              {!isStudent && !isTeacherCabinet && !isParentOnly && (
                <>
                  <HubNavLink
                    item={{
                      id: 'quizzes',
                      label: 'Тесты',
                      icon: 'fa-clipboard-check',
                      to: '/practice-quizzes',
                    }}
                  />
                  <HubNavLink
                    item={{
                      id: 'trainers',
                      label: 'Тренажёры',
                      icon: 'fa-bullseye',
                      to: '/trainers',
                    }}
                  />
                </>
              )}
              {user && !isStudent && !isTeacherCabinet && !isParentOnly && (
                <>
                  {!user.is_teacher && (
                    <HubNavLink
                      item={{
                        id: 'billing',
                        label: 'Баланс',
                        icon: 'fa-wallet',
                        to: '/billing',
                      }}
                    />
                  )}
                  <HubNavLink
                    item={{
                      id: 'konspekt',
                      label: 'Конспект',
                      icon: 'fa-book',
                      to: '/konspekty',
                    }}
                  />
                  <HubNavLink
                    item={{
                      id: 'collab',
                      label: 'Совместное кодирование',
                      icon: 'fa-code',
                      to: user?.collab_session_id
                        ? `/collaborative/${user.collab_session_id}`
                        : '/collaborative',
                    }}
                  />
                </>
              )}
            </nav>
          )}

          {collabChrome && toolbarProps ? (
            <WorkToolbar {...toolbarProps} className="work-toolbar--chrome" />
          ) : null}

          <div className="header-actions">
            <div className="header-theme-toggle" role="group" aria-label="Тема сайта">
              <button
                type="button"
                className={`btn btn-outline btn-xs btn-icon-only ${theme === 'light' ? 'active' : ''}`}
                onClick={() => setTheme('light')}
                title="Светлая тема сайта"
                aria-label="Светлая тема сайта"
              >
                <i className="fa-solid fa-sun ui-icon" aria-hidden />
              </button>
              <button
                type="button"
                className={`btn btn-outline btn-xs btn-icon-only ${isDark ? 'active' : ''}`}
                onClick={() => setTheme('dark')}
                title="Тёмная тема сайта"
                aria-label="Тёмная тема сайта"
              >
                <i className="fa-solid fa-moon ui-icon" aria-hidden />
              </button>
            </div>
            {user ? (
              <UserAccountMenu user={user} onLogout={handleLogout} />
            ) : (
              <div className="auth-links">
                <NavLink to="/login" className="btn btn-outline btn-sm">
                  Войти
                </NavLink>
                <NavLink to="/register" className="btn btn-primary btn-sm">
                  Регистрация
                </NavLink>
              </div>
            )}
          </div>

          {!collabChrome && (
            <button
              type="button"
              ref={menuBtnRef}
              className="mobile-menu-btn"
              aria-expanded={navOpen}
              aria-controls="site-nav"
              aria-label={navOpen ? 'Закрыть меню' : 'Открыть меню'}
              onClick={() => setNavOpen((open) => !open)}
            >
              ☰
            </button>
          )}
        </div>
      </header>
      <main className="main-content">
        {user ? <SiteNoticesBanner /> : null}
        <Outlet />
      </main>
      {!collabChrome && (
        <footer className="footer">
          <p className="footer-operator">
            Оператор ПДн: {OPERATOR_NAME}
            {' · '}ИНН {OPERATOR_INN}
            {' · '}ОГРНИП {OPERATOR_OGRNIP}
          </p>
          <p className="footer-operator">
            Запросы субъектов ПДн:{' '}
            <a href={`mailto:${OPERATOR_EMAIL}`}>{OPERATOR_EMAIL}</a>
          </p>
          <nav className="footer-links" aria-label="Документы">
            <Link to="/privacy">Политика обработки ПДн</Link>
            <Link to="/consent">Согласие на обработку ПДн</Link>
            <Link to={OFERTA_PATH}>Договор-оферта</Link>
            <button
              type="button"
              className="footer-cookie-settings"
              onClick={openCookieSettings}
            >
              Настройки cookie
            </button>
          </nav>
          <p className="footer-cookies">
            Необходимые cookie (сессия и защита форм) ставятся всегда.
            Яндекс.Метрика и Вебвизор — только после согласия в баннере.
          </p>
          <p>© 2026 diobuch — подготовка к ЕГЭ/ОГЭ по информатике</p>
          <p className="footer-build">Обновлено: {BUILD_TAG}</p>
        </footer>
      )}
      <CookieNotice />
    </div>
  );
}

export default function Layout() {
  return (
    <WorkChromeProvider>
      <TabIdentityProvider>
        <LayoutInner />
      </TabIdentityProvider>
    </WorkChromeProvider>
  );
}
